import { useEffect, useId, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';

import css from 'src/utils/css';
import { getFaixaFavorabilidade } from './favorabilidadeHelpers';
import { findLikertRange, LIKERT_COLORS, toNumber } from './likertHelpers';
import LikertSegmentBar from './LikertSegmentBar';

const classes = css`
  .row {
    &:hover {
      background: #f8fafc;
    }
  }

  .favorabilidadeTooltipInner {
    background: transparent;
    padding: 0;
  }
`;

const styles = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #dfe3e8',
    borderRadius: 9,
    marginTop: 16,
    overflow: 'hidden',
  },
  cardTitle: {
    color: '#202124',
    fontSize: 16,
    fontWeight: 700,
    padding: '18px 20px 14px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    borderCollapse: 'collapse',
    minWidth: 860,
    tableLayout: 'fixed',
    width: '100%',
  },
  th: {
    backgroundColor: '#f6f6f7',
    borderBottom: '1px solid #e5e7eb',
    color: '#7b7f87',
    fontSize: 12,
    fontWeight: 400,
    padding: '10px 20px',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },
  thRight: {
    textAlign: 'right',
  },
  td: {
    borderTop: '1px solid #e5e7eb',
    color: '#202124',
    fontSize: 13,
    padding: '12px 20px',
    verticalAlign: 'middle',
  },
  tdDistribution: {
    paddingLeft: 8,
    paddingRight: 8,
  },
  tdRight: {
    color: '#7b7f87',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  question: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    cursor: 'default',
    display: '-webkit-box',
    lineHeight: '18px',
    maxHeight: 36,
    overflow: 'hidden',
    overflowWrap: 'break-word',
  },
  result: {
    fontWeight: 700,
  },
  favorabilidadeValue: {
    cursor: 'help',
    display: 'inline-block',
  },
  faixaBadge: {
    borderRadius: 999,
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 8px',
    whiteSpace: 'nowrap',
  },
};

const getResultColor = (ranges) => {
  const dominantRange = ['negativa', 'neutra', 'positiva'].reduce(
    (current, code) => {
      const percentual = toNumber(findLikertRange(ranges, code).percentual);

      return percentual > current.percentual ? { code, percentual } : current;
    },
    { code: null, percentual: 0 }
  );

  return dominantRange.code ? LIKERT_COLORS[dominantRange.code] : '#7b7f87';
};

function TruncatedQuestion({ text }) {
  const elementRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const reactId = useId();
  const tooltipId = `likert-question-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useEffect(() => {
    const node = elementRef.current;

    if (!node) return undefined;

    const updateTruncation = () => {
      setIsTruncated(
        node.scrollHeight > node.clientHeight ||
          node.scrollWidth > node.clientWidth
      );
    };

    updateTruncation();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(node);

    return () => observer.disconnect();
  }, [text]);

  return (
    <>
      <div id={tooltipId} ref={elementRef} style={styles.question}>
        {text}
      </div>
      {isTruncated && (
        <UncontrolledTooltip
          container="body"
          innerClassName="text-left"
          placement="top"
          target={tooltipId}
        >
          {text}
        </UncontrolledTooltip>
      )}
    </>
  );
}

function LikertPorPerguntaTabela({ faixasFavorabilidade, perguntas }) {
  const intl = useIntl();
  const questions = [...(perguntas || [])].sort(
    (a, b) => toNumber(a.ordem) - toNumber(b.ordem)
  );

  if (questions.length === 0) return null;

  const formatResult = (value) =>
    value == null
      ? '–'
      : `${intl.formatNumber(toNumber(value), {
          maximumFractionDigits: 1,
        })}%`;

  const renderFavorabilidade = (favorabilidade, tooltipId) => {
    const value = favorabilidade?.favorabilidadePercentual;

    if (value == null) return '–';

    const faixa = getFaixaFavorabilidade(value, faixasFavorabilidade);
    const label = intl.formatMessage({ id: faixa.labelId });

    return (
      <>
        <span
          aria-label={`${formatResult(value)} - ${label}`}
          id={tooltipId}
          style={{
            ...styles.result,
            ...styles.favorabilidadeValue,
            color: faixa.color,
          }}
          tabIndex={0}
        >
          {formatResult(value)}
        </span>
        <UncontrolledTooltip
          container="body"
          hideArrow
          innerClassName={classes.favorabilidadeTooltipInner}
          placement="top"
          target={tooltipId}
        >
          <span style={{ ...styles.faixaBadge, backgroundColor: faixa.color }}>
            {label}
          </span>
        </UncontrolledTooltip>
      </>
    );
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>
        {intl.formatMessage({
          id: 'labelDistribuicaoLikertPorPergunta',
        })}
      </div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '28%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={styles.th}>
                {intl.formatMessage({ id: 'labelPergunta' })}
              </th>
              <th style={styles.th}>
                {intl.formatMessage({ id: 'labelDistribuicao' })}
              </th>
              <th style={{ ...styles.th, ...styles.thRight }}>
                {intl.formatMessage({ id: 'labelFavorabilidade' })}
              </th>
              <th style={{ ...styles.th, ...styles.thRight }}>
                {intl.formatMessage({ id: 'labelResultado' })}
              </th>
              <th style={{ ...styles.th, ...styles.thRight }}>
                {intl.formatMessage({ id: 'labelRespostas' })}
              </th>
              <th style={{ ...styles.th, ...styles.thRight }}>
                {intl.formatMessage({ id: 'labelComentarios' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {questions.map((question, index) => (
              <tr className={classes.row} key={question.perguntaId ?? index}>
                <td style={styles.td}>
                  <TruncatedQuestion text={question.titulo || ''} />
                </td>
                <td
                  style={{
                    ...styles.td,
                    ...styles.tdDistribution,
                  }}
                >
                  <LikertSegmentBar faixas={question.faixas} height={20} />
                </td>
                <td style={{ ...styles.td, ...styles.tdRight }}>
                  {renderFavorabilidade(
                    question.favorabilidade,
                    `likert-favorabilidade-${String(
                      question.perguntaId ?? index
                    ).replace(/[^a-zA-Z0-9_-]/g, '')}-${index}`
                  )}
                </td>
                <td style={{ ...styles.td, ...styles.tdRight }}>
                  <span
                    style={{
                      ...styles.result,
                      color: getResultColor(question.faixas),
                    }}
                  >
                    {formatResult(question.resultadoPercentual)}
                  </span>
                </td>
                <td style={{ ...styles.td, ...styles.tdRight }}>
                  {toNumber(question.totalRespostas)}
                </td>
                <td style={{ ...styles.td, ...styles.tdRight }}>
                  {toNumber(question.totalComentarios)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default LikertPorPerguntaTabela;
